import { components, useApi } from "@appretio/api";
import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import { useImperativeFetch } from "@appretio/shared";
import { useCallback } from "react";

export type ForecastGridDataOutput = components["schemas"]["GridDataOutput"];

/**
 * Parameters for loading the temporal grid.
 *
 * ``cubeIds`` is a flat list of cube identifiers — UUIDs for user/budget
 * cubes, or DS version strings for analytics sources. The server buckets
 * them by producer via ``resolve_producer``; no producer-keyed structure
 * is needed on the wire.
 */
export interface FetchForecastDataParams {
  /** Context filter selections. */
  context: ForecastContextSelectedOptions;
  /** Flat list of cube identifiers. */
  cubeIds: string[];
  /**
   * The writable own cube among ``cubeIds``. Required by the server when
   * several own cubes load together — its rows get the "user" label.
   */
  saveCubeId?: string;
}

export function useFetchForecastData() {
  const apiClient = useApi();

  const fetchFn = useCallback(
    async ({
      context,
      cubeIds,
      saveCubeId
    }: FetchForecastDataParams): Promise<ForecastGridDataOutput> => {
      const response = await apiClient.POST("/v2/forecast/grid/get-data", {
        body: {
          context: context ?? {},
          cube_ids: cubeIds,
          save_cube_id: saveCubeId
        }
      });
      const result = response.data?.result;
      if (!result) {
        throw new Error("Missing result in forecast data response");
      }
      return result;
    },
    [apiClient]
  );

  return useImperativeFetch(fetchFn, {
    errorMessage: "Failed to load forecast data"
  });
}
